import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
  Grid,
  Alert,
  Checkbox,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { expensesAPI, referenceAPI, pendingReceiptsAPI, getMediaUrl } from '../services/api';
import { formatDate, formatUtcDate } from '../utils/dateFormat';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import { cardSx } from '../theme';
import { SummaryTotalCard, SummaryTile, SummaryStripSkeleton, summaryStripSx, summaryPanelMinHeight, tabularNumsSx } from '../components/CategorySummary';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import useRefreshOnFocus from '../utils/useRefreshOnFocus';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import AddExpenseTypeModal from '../components/AddExpenseTypeModal';
import AddExpenseLocationModal from '../components/AddExpenseLocationModal';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

// Both dimensions mirror backend/routes/receipt_scanner.py, which downscales to
// them server-side regardless — so resizing to the same bound before upload
// costs no quality and saves the whole difference in transfer time. A phone
// camera photo runs 3-10MB; these bring it to roughly 200-400KB (scan) and
// 400-800KB (stored).
const SCAN_MAX_DIMENSION = 1280;   // MAX_SCAN_DIMENSION — the copy sent to Claude
const STORED_MAX_DIMENSION = 1920; // MAX_DIMENSION — the copy kept on disk
const RESIZE_JPEG_QUALITY = 0.85;

// Mirrors expenses.summary, VARCHAR(255). summarizeItems() below joins every
// included line item, so a long itemized receipt overran the column and the
// save came back as a raw MySQL error.
const SUMMARY_MAX_LENGTH = 255;

// Resizing is a transfer optimization, not a correctness requirement — the
// backend downscales whatever it receives. So a file already small enough to
// upload quickly is sent untouched rather than spending phone CPU to shrink it
// further. Only camera-sized photos are worth the work.
const RESIZE_SKIP_BYTES = 1_500_000;

/** Encode a canvas or OffscreenCanvas to a JPEG blob. */
const canvasToJpegBlob = (canvas) => {
  if (typeof canvas.convertToBlob === 'function') {
    return canvas.convertToBlob({ type: 'image/jpeg', quality: RESIZE_JPEG_QUALITY });
  }
  return new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', RESIZE_JPEG_QUALITY);
  });
};

/**
 * Downscale an image file to a JPEG no larger than maxDimension on its long
 * edge, preferring paths that keep the work off the main thread.
 *
 * `createImageBitmap` decodes off-thread and downscales during decode, which
 * matters a great deal on a phone: modern cameras produce 12-48MP files, and
 * decoding one of those into an <img> and drawing it at full resolution can
 * block the UI for longer than the upload it is meant to save. The <img> path
 * is kept only as a fallback for browsers without it.
 *
 * PDFs are returned untouched — canvas cannot rasterize them, and the backend
 * already renders their first page with PyMuPDF. Any failure also falls back to
 * the original file: a slow upload beats one that refuses to run.
 */
const resizeImage = async (file, maxDimension) => {
  if (!file || file.type === 'application/pdf' || !file.type?.startsWith('image/')) {
    return file;
  }
  if (file.size <= RESIZE_SKIP_BYTES) {
    return file;
  }

  const started = performance.now();
  const asJpegFile = (blob) => {
    const name = file.name.replace(/\.[^.]+$/, '') || 'receipt';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg' });
  };

  try {
    let blob = null;

    if (typeof createImageBitmap === 'function') {
      const source = await createImageBitmap(file);
      const longEdge = Math.max(source.width, source.height);
      const scale = longEdge ? Math.min(1, maxDimension / longEdge) : 1;
      const width = Math.max(1, Math.round(source.width * scale));
      const height = Math.max(1, Math.round(source.height * scale));

      // Re-decode at the target size where supported, so the full-resolution
      // bitmap never has to be drawn.
      let bitmap = source;
      if (scale < 1) {
        bitmap = await createImageBitmap(source, {
          resizeWidth: width,
          resizeHeight: height,
          resizeQuality: 'high',
        });
        source.close?.();
      }

      const canvas = typeof OffscreenCanvas === 'function'
        ? new OffscreenCanvas(width, height)
        : Object.assign(document.createElement('canvas'), { width, height });
      canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
      bitmap.close?.();
      blob = await canvasToJpegBlob(canvas);
    } else {
      blob = await new Promise((resolve) => {
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = async () => {
          URL.revokeObjectURL(objectUrl);
          const longEdge = Math.max(img.width, img.height);
          const scale = longEdge ? Math.min(1, maxDimension / longEdge) : 1;
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(img.width * scale));
          canvas.height = Math.max(1, Math.round(img.height * scale));
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(await canvasToJpegBlob(canvas));
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(null);
        };
        img.src = objectUrl;
      });
    }

    if (!blob) {
      return file;
    }
    const resized = asJpegFile(blob);
    console.info(
      `[receipt] resized ${(file.size / 1024).toFixed(0)}KB -> `
      + `${(resized.size / 1024).toFixed(0)}KB in ${(performance.now() - started).toFixed(0)}ms`,
    );
    return resized;
  } catch (err) {
    console.warn('Receipt resize failed, sending original:', err);
    return file;
  }
};

const normalizeId = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// Both reference lists are reloaded from two places each — the page load and the
// "add new" modal's onSuccess — so the ordering lives here rather than in four copies.
const sortExpenseTypes = (payload) => [...toArray(payload, 'expense types')].sort((a, b) => {
  if (a.sort_order !== b.sort_order) {
    return (a.sort_order || 0) - (b.sort_order || 0);
  }
  if (a.category !== b.category) {
    return (a.category || '').localeCompare(b.category || '');
  }
  return (a.name || '').localeCompare(b.name || '');
});

const sortExpenseLocations = (payload) => [...toArray(payload, 'expense locations')].sort((a, b) => {
  if (a.sort_order !== b.sort_order) {
    return (a.sort_order || 0) - (b.sort_order || 0);
  }
  if (a.name !== b.name) {
    return (a.name || '').localeCompare(b.name || '');
  }
  return (a.location || '').localeCompare(b.location || '');
});

// The "Saved this batch" side panel is fixed-height so the dialog itself never grows
// as rows are added — but a plain pixel height always ends up slicing whichever row
// lands on the boundary once enough rows exist. Pinning each row's own height lets the
// box be an exact multiple of it instead, so it only ever cuts between rows, never mid-row.
const BATCH_ROW_HEIGHT = 32;
const BATCH_ROW_GAP = 8;
const BATCH_ROWS_VISIBLE = 7;

const ExpensesPage = () => {
  const notify = useNotify();
  const currentYear = new Date().getFullYear();
  const [expenses, setExpenses] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [expenseLocations, setExpenseLocations] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [openDialog, setOpenDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortBy, setSortBy] = useState('expense_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modals for adding reference data
  const [openExpenseTypeModal, setOpenExpenseTypeModal] = useState(false);
  const [openExpenseLocationModal, setOpenExpenseLocationModal] = useState(false);

  // Receipt scanner state
  const receiptInputRef = useRef(null);
  const receiptLibraryInputRef = useRef(null);
  // capture="environment" is honored only when the accept list resolves to a
  // single capture-capable type, so PDFs are offered on pointer devices only —
  // on a phone that extra type is what turns a camera launch into an OS
  // "Select action" chooser. `hover: none` keeps touchscreen laptops, which are
  // also pointer:coarse, on the desktop path.
  const cameraFirst = useMediaQuery('(pointer: coarse) and (hover: none)');
  // iOS's own file-input sheet (Take Photo / Photo Library / Browse) already
  // covers both capture and selection, so a second button there would just
  // duplicate it — Android's chooser has no such built-in library option, so
  // it gets a direct-camera button plus a separate load-file button instead.
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanError, setScanError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [pendingReceiptFile, setPendingReceiptFile] = useState(null);
  const [receiptUploadWarning, setReceiptUploadWarning] = useState('');
  const [suggestedTypeName, setSuggestedTypeName] = useState(null);
  const [suggestedTypeCategoryName, setSuggestedTypeCategoryName] = useState(null);
  const [suggestedLocationName, setSuggestedLocationName] = useState(null);
  const [suggestedLocationCityState, setSuggestedLocationCityState] = useState(null);
  const [potentialDuplicate, setPotentialDuplicate] = useState(null);
  const [scannedItems, setScannedItems] = useState([]);
  const [scannedTaxAmount, setScannedTaxAmount] = useState(null);

  // Emailed and texted receipts awaiting review
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [activePendingReceiptId, setActivePendingReceiptId] = useState(null);

  const [viewingReceiptFilename, setViewingReceiptFilename] = useState(null);
  const activePendingReceipt = pendingReceipts.find((r) => r.id === activePendingReceiptId);

  const [formData, setFormData] = useState({
    expense_date: '',
    expense_time: '',
    expense_type_id: '',
    expense_location_id: '',
    amount: '',
    summary: '',
    notes: '',
    payee: '',
    receipt_number: '',
  });

  const yearOptions = useMemo(() => (
    Array.from({ length: 11 }, (_, index) => String(currentYear - index))
  ), [currentYear]);

  const expenseLocationOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...expenseLocations.map((loc) => ({
      id: loc.id,
      label: `${loc.name}${loc.location ? ` - ${loc.location}` : ''}${loc.online ? ' (Online)' : ''}`,
    })),
  ], [expenseLocations]);

  useEffect(() => {
    loadPendingReceipts();
    const intervalId = setInterval(loadPendingReceipts, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const loadPendingReceipts = async () => {
    try {
      const { data } = await pendingReceiptsAPI.getAll({ status: 'all' });
      const rows = toArray(data, 'pending-receipts');
      setPendingReceipts(rows.filter((r) => r.status === 'pending' || r.status === 'error'));
    } catch (error) {
      console.error('Error loading pending receipts:', error);
    }
  };

  const loadData = useCallback(async () => {
    setLoadError('');
    try {
      const listParams = {};
      const summaryParams = {};

      if (selectedYear) {
        listParams.start_date = `${selectedYear}-01-01`;
        listParams.end_date = `${selectedYear}-12-31`;
        summaryParams.year = selectedYear;
      }

      // allSettled rather than all: four independent endpoints, and one of them dying
      // should cost its own list rather than discarding the three that answered.
      const results = await Promise.allSettled([
        expensesAPI.getAll(listParams),
        expensesAPI.getSummary(summaryParams),
        referenceAPI.getExpenseTypes(),
        referenceAPI.getExpenseLocations(),
      ]);
      const [expensesRes, summaryRes, typesRes, locationsRes] = results;

      // Every payload is normalized before it reaches state. A stored non-array does not
      // fail the fetch — it throws later, inside the render that spreads and sorts it,
      // and an exception there unmounts the whole app instead of one list.
      if (expensesRes.status === 'fulfilled') {
        setExpenses(toArray(expensesRes.value?.data, 'expenses'));
      }
      if (summaryRes.status === 'fulfilled') {
        setSummary(toArray(summaryRes.value?.data, 'expense summary'));
      }
      if (typesRes.status === 'fulfilled') {
        setExpenseTypes(sortExpenseTypes(typesRes.value?.data));
      }
      if (locationsRes.status === 'fulfilled') {
        setExpenseLocations(sortExpenseLocations(locationsRes.value?.data));
      }

      const failure = results.find((result) => result.status === 'rejected');
      if (failure) {
        console.error('Error loading data:', failure.reason);
        // Whatever loaded is left on screen on purpose: a failed refresh should say so
        // rather than blank the page out into something indistinguishable from "no expenses".
        setLoadError(
          failure.reason?.response?.data?.error || 'Unable to load expenses. Please try again.'
        );
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setLoadError(error?.response?.data?.error || 'Unable to load expenses. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // The 60s poller above only runs while the tab is focused — browsers throttle it when
  // hidden and freeze it on mobile — so returning to the page refetches both.
  useRefreshOnFocus(() => {
    loadData();
    loadPendingReceipts();
  });

  const expenseTypeMap = useMemo(() => {
    const map = new Map();
    expenseTypes.forEach((type) => {
      if (type?.id !== undefined && type?.id !== null) {
        map.set(type.id, type);
      }
    });
    return map;
  }, [expenseTypes]);

  const expenseLocationMap = useMemo(() => {
    const map = new Map();
    expenseLocations.forEach((loc) => {
      if (loc?.id !== undefined && loc?.id !== null) {
        map.set(loc.id, loc);
      }
    });
    return map;
  }, [expenseLocations]);

  const [batchSavedExpenses, setBatchSavedExpenses] = useState([]);

  const handleOpenDialog = (expense = null) => {
    setBatchSavedExpenses([]);
    if (expense) {
      setEditingExpense(expense);
      // Format time for input (HH:MM)
      let timeValue = '';
      if (expense.expense_time) {
        const timeStr = expense.expense_time;
        if (timeStr.includes(':')) {
          const parts = timeStr.split(':');
          timeValue = `${parts[0]}:${parts[1]}`;
        }
      }
      setFormData({
        expense_date: expense.expense_date || '',
        expense_time: timeValue,
        expense_type_id: expense.expense_type_id || '',
        expense_location_id: expense.expense_location_id || '',
        amount: expense.amount || '',
        summary: expense.summary || '',
        notes: expense.notes || '',
        payee: expense.payee || '',
        receipt_number: expense.receipt_number || '',
      });
      setScannedItems([]);
      setScannedTaxAmount(null);
    } else {
      setEditingExpense(null);
      setFormData({
        expense_date: '',
        expense_time: '',
        expense_type_id: '',
        expense_location_id: '',
        amount: '',
        summary: '',
        notes: '',
        payee: '',
        receipt_number: '',
      });
      setScannedItems([]);
      setScannedTaxAmount(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingExpense(null);
    setBatchSavedExpenses([]);
    setPendingReceiptFile(null);
    setActivePendingReceiptId(null);
    setScanError('');
    setSuggestedTypeName(null);
    setSuggestedTypeCategoryName(null);
    setSuggestedLocationName(null);
    setSuggestedLocationCityState(null);
    setPotentialDuplicate(null);
    setScannedItems([]);
    setScannedTaxAmount(null);
  };

  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

  const recalculateAmount = (items, taxAmount) => {
    const taxableSubtotal = items.reduce((sum, i) => (i.taxable ? sum + i.price : sum), 0);
    const taxRate = taxableSubtotal > 0 && taxAmount ? taxAmount / taxableSubtotal : 0;
    const keptItems = items.filter((i) => i.included);
    const keptSubtotal = keptItems.reduce((sum, i) => sum + i.price, 0);
    const keptTaxableSubtotal = keptItems.reduce((sum, i) => (i.taxable ? sum + i.price : sum), 0);
    return round2(keptSubtotal + round2(keptTaxableSubtotal * taxRate));
  };

  // Take as many item descriptions as fit in the column and account for the rest
  // with a "+N more" tail, so a long itemized receipt still yields a readable
  // one-line summary instead of a string the backend has to reject.
  const summarizeItems = (items) => {
    const descriptions = items.filter((i) => i.included).map((i) => i.description);

    const kept = [];
    let length = 0;
    for (const description of descriptions) {
      const remaining = descriptions.length - kept.length - 1;
      const tail = remaining > 0 ? ` +${remaining} more` : '';
      const added = (kept.length ? 2 : 0) + description.length;
      if (length + added + tail.length > SUMMARY_MAX_LENGTH) break;
      kept.push(description);
      length += added;
    }

    // Nothing fit — a single description longer than the whole budget. Cut it
    // down rather than returning a bare "+1 more".
    if (kept.length === 0) {
      return descriptions.length ? descriptions[0].slice(0, SUMMARY_MAX_LENGTH) : '';
    }

    const dropped = descriptions.length - kept.length;
    return kept.join(', ') + (dropped > 0 ? ` +${dropped} more` : '');
  };

  const handleToggleScannedItem = (index) => {
    setScannedItems((prev) => {
      const next = prev.map((item, i) => (i === index ? { ...item, included: !item.included } : item));
      setFormData((formPrev) => ({
        ...formPrev,
        amount: String(recalculateAmount(next, scannedTaxAmount)),
        summary: summarizeItems(next),
      }));
      return next;
    });
  };

  // null means "use the main Expense Type field" — only set once a user picks a
  // different type for this specific item, which is what turns a scan into a split.
  const handleSetScannedItemType = (index, typeId) => {
    setScannedItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, expense_type_id: typeId } : item)));
  };

  // Buckets included items by resolved expense type (per-item override, falling back
  // to the main Expense Type field) and totals each bucket — reusing the same tax
  // apportionment as recalculateAmount, but computed once across every included item
  // so the per-group amounts still sum to the full receipt total.
  const groupedItems = useMemo(() => {
    const included = scannedItems.filter((i) => i.included);
    if (included.length === 0) return [];

    const totalTaxableSubtotal = included.reduce((sum, i) => (i.taxable ? sum + i.price : sum), 0);
    const taxRate = totalTaxableSubtotal > 0 && scannedTaxAmount ? scannedTaxAmount / totalTaxableSubtotal : 0;

    const byType = new Map();
    included.forEach((item) => {
      const typeId = item.expense_type_id ?? formData.expense_type_id ?? '';
      if (!byType.has(typeId)) byType.set(typeId, []);
      byType.get(typeId).push(item);
    });

    return Array.from(byType.entries()).map(([typeId, items]) => {
      const subtotal = items.reduce((sum, i) => sum + i.price, 0);
      const taxableSubtotal = items.reduce((sum, i) => (i.taxable ? sum + i.price : sum), 0);
      return {
        typeId,
        amount: round2(subtotal + round2(taxableSubtotal * taxRate)),
        summary: summarizeItems(items),
      };
    });
  }, [scannedItems, scannedTaxAmount, formData.expense_type_id]);

  const isSplit = groupedItems.length > 1;
  const hasUnassignedGroup = isSplit && groupedItems.some((g) => !g.typeId);

  const normalizeStr = (s) => (s || '').trim().toLowerCase();

  const baseName = (s) => normalizeStr(s).split('(')[0].trim();
  const parenContent = (s) => {
    const m = normalizeStr(s).match(/\(([^)]+)\)/);
    return m ? m[1].trim() : '';
  };

  const findMatchingExpenseType = (name, types) => {
    const target = normalizeStr(name);
    if (!target) return null;
    const exact = types.filter((t) => normalizeStr(t.name) === target);
    if (exact.length === 1) return exact[0];
    if (exact.length > 1) return null; // ambiguous exact matches — don't guess

    const targetBase = baseName(name);
    const targetParen = parenContent(name);
    const loose = types.filter((t) => {
      const typeBase = baseName(t.name);
      const typeParen = parenContent(t.name);
      return (
        (targetBase && typeBase === targetBase) ||
        (targetParen && typeBase === targetParen) ||
        (typeParen && targetBase === typeParen)
      );
    });
    return loose.length === 1 ? loose[0] : null;
  };

  const findMatchingLocation = (name, cityState, locations) => {
    const targetName = normalizeStr(name);
    if (!targetName) return null;
    const matches = locations.filter((loc) => normalizeStr(loc.name) === targetName);
    if (matches.length === 1) return matches[0];
    if (matches.length > 1) {
      const targetCityState = normalizeStr(cityState);
      if (!targetCityState) return null; // ambiguous, can't disambiguate — don't guess
      const narrowed = matches.filter((loc) => normalizeStr(loc.location) === targetCityState);
      return narrowed.length === 1 ? narrowed[0] : null;
    }
    return null;
  };

  const applyScanResult = (data) => {
    setFormData((prev) => ({
      ...prev,
      expense_date: data.expense_date ?? prev.expense_date,
      expense_time: data.expense_time ?? prev.expense_time,
      amount: data.amount != null ? String(data.amount) : prev.amount,
      summary: data.summary ?? prev.summary,
      payee: data.payee ?? prev.payee,
      receipt_number: data.receipt_number ?? prev.receipt_number,
      expense_type_id: data.expense_type_id ?? prev.expense_type_id,
      expense_location_id: data.expense_location_id ?? prev.expense_location_id,
    }));
    if (Array.isArray(data.items) && data.items.length > 0) {
      setScannedItems(data.items.map((item) => ({ ...item, included: true })));
      setScannedTaxAmount(data.tax_amount ?? null);
    }
    if (data.expense_type_id == null && data.suggested_expense_type_name) {
      const rawName = data.suggested_expense_type_name;
      const colonIdx = rawName.indexOf(': ');
      const cleanName = colonIdx > -1 ? rawName.slice(colonIdx + 2) : rawName;
      const matchedType = findMatchingExpenseType(cleanName, expenseTypes);
      if (matchedType) {
        setFormData((prev) => ({ ...prev, expense_type_id: matchedType.id }));
      } else {
        setSuggestedTypeName(cleanName);
        setSuggestedTypeCategoryName(data.suggested_expense_type_category || null);
      }
    }
    if (data.expense_location_id == null && data.suggested_location_name) {
      const matchedLocation = findMatchingLocation(
        data.suggested_location_name,
        data.suggested_location_city_state,
        expenseLocations,
      );
      if (matchedLocation) {
        setFormData((prev) => ({ ...prev, expense_location_id: matchedLocation.id }));
      } else {
        setSuggestedLocationName(data.suggested_location_name);
        setSuggestedLocationCityState(data.suggested_location_city_state || null);
      }
    }
    if (data.potential_duplicate) {
      setPotentialDuplicate(data.potential_duplicate);
    }
  };

  const handleScanReceipt = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setScanError('');
    setSuggestedTypeName(null);
    setSuggestedTypeCategoryName(null);
    setSuggestedLocationName(null);
    setSuggestedLocationCityState(null);
    setPotentialDuplicate(null);
    setScannedItems([]);
    setScannedTaxAmount(null);
    setScanning(true);

    // Timing is reported below for both outcomes: a scan that fails slowly is
    // exactly as interesting as one that succeeds slowly.
    const tStart = performance.now();
    let tRequest = tStart;
    let scanFile = file;
    let outcome = 'error';

    try {
      // Scan the smaller copy, but keep the original for storage — it is
      // resized separately at save time, to the larger STORED_MAX_DIMENSION.
      scanFile = await resizeImage(file, SCAN_MAX_DIMENSION);
      tRequest = performance.now();
      const { data } = await expensesAPI.scanReceipt(scanFile);
      outcome = 'ok';
      applyScanResult(data);
      setPendingReceiptFile(file);
    } catch (err) {
      // The backend reports its own failures as {"error": ...}. Anything else
      // died at the proxy or the network, where the status code is the only
      // clue there is — so name it rather than hiding it behind one string.
      const status = err.response?.status;
      setScanError(
        err.response?.data?.error
        || (status === 413
          ? 'That image is too large to upload. Try a smaller photo.'
          : status
            ? `Receipt scan failed (HTTP ${status}). Please try again.`
            : 'Receipt scan failed — could not reach the server. Please try again.'),
      );
    } finally {
      setScanning(false);

      // A phone has no reachable console, so the numbers that only the browser
      // knows — resize cost and round-trip time — are sent to the server to be
      // logged beside its own. Never allowed to disturb the scan itself.
      const timing = {
        original_bytes: file.size,
        sent_bytes: scanFile?.size ?? file.size,
        resize_ms: Math.round(tRequest - tStart),
        request_ms: Math.round(performance.now() - tRequest),
        outcome,
      };
      console.info('[receipt] scan timing', timing);
      expensesAPI.logScanTiming(timing).catch(() => { });
    }
  };

  const handleReviewPendingReceipt = (item) => {
    handleOpenDialog();
    if (item.extracted_json) {
      applyScanResult(item.extracted_json);
    }
    setActivePendingReceiptId(item.id);
  };

  const handleDismissPendingReceipt = async (id) => {
    try {
      await pendingReceiptsAPI.dismiss(id);
      loadPendingReceipts();
    } catch (error) {
      console.error('Error dismissing pending receipt:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const performSave = async (keepOpen) => {
    setReceiptUploadWarning('');
    setSaving(true);
    try {
      let savedExpenses;
      if (editingExpense) {
        const res = await expensesAPI.update(editingExpense.id, formData);
        savedExpenses = [res.data];
      } else if (isSplit) {
        // One POST per distinct expense type on the receipt, each carrying its
        // own computed amount/summary but the shared invoice-level fields.
        savedExpenses = [];
        for (const group of groupedItems) {
          const res = await expensesAPI.create({
            ...formData,
            expense_type_id: group.typeId,
            amount: String(group.amount),
            summary: group.summary,
          });
          savedExpenses.push(res.data);
        }
      } else {
        const res = await expensesAPI.create(formData);
        savedExpenses = [res.data];
      }

      if (pendingReceiptFile) {
        // This upload blocks the dialog closing, so it is what the user
        // waits on after pressing Save. Sending the raw camera file made
        // that wait the length of a 3-10MB mobile upload, for an image the
        // backend then downscales to STORED_MAX_DIMENSION anyway.
        const storedFile = await resizeImage(pendingReceiptFile, STORED_MAX_DIMENSION);
        for (const savedExpense of savedExpenses) {
          try {
            await expensesAPI.uploadReceipt(savedExpense.id, storedFile);
          } catch (uploadErr) {
            // The expense itself saved, so this must not fail the save — but it
            // must not pass silently either. A receipt that never attached is
            // otherwise discovered at tax time.
            console.warn('Receipt image upload failed:', uploadErr);
            const status = uploadErr.response?.status;
            setReceiptUploadWarning(
              `The expense saved, but its receipt image did not upload${status ? ` (HTTP ${status})` : ''
              }. Re-attach it by editing the expense.`,
            );
          }
        }
      }
      if (activePendingReceiptId && savedExpenses.length > 0) {
        try {
          await pendingReceiptsAPI.approve(activePendingReceiptId, {
            expense_ids: savedExpenses.map((e) => e.id),
          });
        } catch (approveErr) {
          console.warn('Could not link pending receipt:', approveErr);
        }
        loadPendingReceipts();
      }
      if (keepOpen) {
        // Batch entry (e.g. 12 months of the same electric bill): leave
        // formData exactly as just saved so the user only edits whatever
        // differs (typically date and amount), then saves again. Only the
        // state tied to the receipt/scan just processed gets cleared.
        setBatchSavedExpenses((prev) => [
          ...prev,
          ...savedExpenses.map((e) => ({ id: e.id, expense_date: e.expense_date, amount: e.amount })),
        ]);
        setPendingReceiptFile(null);
        setActivePendingReceiptId(null);
        setScanError('');
        setSuggestedTypeName(null);
        setSuggestedTypeCategoryName(null);
        setSuggestedLocationName(null);
        setSuggestedLocationCityState(null);
        setPotentialDuplicate(null);
        setScannedItems([]);
        setScannedTaxAmount(null);
      } else {
        handleCloseDialog();
      }
      // The Year filter can hide a record whose date falls outside it — switch
      // to the saved record's year instead of just reloading under the old one.
      const savedYear = savedExpenses[0]?.expense_date?.slice(0, 4);
      if (savedYear && savedYear !== selectedYear) {
        setSelectedYear(savedYear);
      } else {
        loadData();
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      notify.error(errorMessage(error, 'Error saving expense'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => performSave(false);
  const handleSubmitAndAddAnother = () => performSave(true);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (id) => setDeleteTarget(id);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await expensesAPI.delete(deleteTarget);
      setBatchSavedExpenses((prev) => prev.filter((e) => e.id !== deleteTarget));
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting expense:', error);
      notify.error(errorMessage(error, 'Error deleting expense'));
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return timeStr;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '0.00';
    }
    return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getExpenseCategoryDisplay = useCallback((expense) => {
    if (!expense) return '-';
    const typeId = normalizeId(expense.expense_type_id);
    const type = typeId !== null ? expenseTypeMap.get(typeId) : null;
    return type?.category || expense.expense_type || '-';
  }, [expenseTypeMap]);

  const getExpenseTypeDisplay = useCallback((expense) => {
    if (!expense) return '-';
    const typeId = normalizeId(expense.expense_type_id);
    const type = typeId !== null ? expenseTypeMap.get(typeId) : null;
    return type?.name || '-';
  }, [expenseTypeMap]);

  const getExpenseLocationDisplay = useCallback((expense) => {
    if (!expense) return '-';
    const locationId = normalizeId(expense.expense_location_id);
    const location = locationId !== null ? expenseLocationMap.get(locationId) : null;
    if (location) {
      let label = location.name || '';
      if (location.location) {
        label += `${label ? ' - ' : ''}${location.location}`;
      }
      if (location.online) {
        label += `${label ? ' ' : ''}(Online)`;
      }
      return label || '-';
    }
    return expense.expense_location || '-';
  }, [expenseLocationMap]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const sortedExpenses = useMemo(() => {
    const getSortValue = (expense) => {
      switch (sortBy) {
        case 'category':
          return getExpenseCategoryDisplay(expense);
        case 'expense_location':
          return getExpenseLocationDisplay(expense);
        case 'expense_time':
          return expense.expense_time || '';
        case 'amount':
          return expense.amount || 0;
        case 'summary':
          return expense.summary || '';
        case 'expense_type':
          return getExpenseTypeDisplay(expense);
        case 'expense_date':
        default:
          return expense.expense_date || '';
      }
    };

    const sorted = [...expenses].sort((a, b) => {
      const aVal = getSortValue(a);
      const bVal = getSortValue(b);
      const result = typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));
      return sortOrder === 'asc' ? result : -result;
    });
    return sorted;
  }, [expenses, sortBy, sortOrder, getExpenseCategoryDisplay, getExpenseLocationDisplay, getExpenseTypeDisplay]);

  const handleCategoryClick = (categoryName) => {
    if (categoryName === selectedCategory) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(categoryName);
    }
  };

  const getCategoryBreakdown = useMemo(() => {
    if (!selectedCategory) return [];

    // Filter expenses by selected category
    const filteredExpenses = expenses.filter((expense) => {
      // Check category_name if available
      if (expense.category_name === selectedCategory) {
        return true;
      }
      // Check via expense type's category
      const typeId = normalizeId(expense.expense_type_id);
      if (typeId !== null) {
        const type = expenseTypeMap.get(typeId);
        if (type && type.category === selectedCategory) {
          return true;
        }
      }
      return false;
    });

    // Group by expense_type_id and sum amounts
    const grouped = new Map();
    filteredExpenses.forEach((expense) => {
      const typeId = normalizeId(expense.expense_type_id);
      if (typeId !== null) {
        const type = expenseTypeMap.get(typeId);
        if (type) {
          const key = typeId;
          if (!grouped.has(key)) {
            grouped.set(key, {
              expenseTypeId: typeId,
              expenseTypeName: type.name, // Just the name, not "category: name"
              total: 0,
            });
          }
          const item = grouped.get(key);
          item.total += expense.amount || 0;
        }
      }
    });

    // Largest first, matching the category strip: the question a summary answers
    // is "where did the money go", and alphabetical order buries that.
    return Array.from(grouped.values()).sort((a, b) =>
      (b.total || 0) - (a.total || 0) || a.expenseTypeName.localeCompare(b.expenseTypeName)
    );
  }, [expenses, selectedCategory, expenseTypeMap]);

  const sortedSummary = useMemo(() => (
    [...summary].sort((a, b) => (b.total || 0) - (a.total || 0)
      || String(a.category || '').localeCompare(String(b.category || '')))
  ), [summary]);

  // Proportion bars are sized against the biggest tile in their own strip, so a
  // strip of small numbers still reads as a comparison instead of five stubs.
  const maxCategoryTotal = useMemo(
    () => sortedSummary.reduce((max, item) => Math.max(max, item.total || 0), 0),
    [sortedSummary],
  );
  const maxBreakdownTotal = useMemo(
    () => getCategoryBreakdown.reduce((max, item) => Math.max(max, item.total || 0), 0),
    [getCategoryBreakdown],
  );

  const totalExpenses = expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);


  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        {loadError && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {loadError}
          </Alert>
        )}
        {receiptUploadWarning && (
          <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setReceiptUploadWarning('')}>
            {receiptUploadWarning}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pt: 0.5, gap: 1 }}>
          <Typography variant="h4">Expenses</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Autocomplete
              id="expenses-year-select"
              size="small"
              disableClearable
              sx={{ minWidth: { xs: 110, sm: 160 } }}
              options={['', ...yearOptions]}
              getOptionLabel={(year) => year || 'All Years'}
              filterOptions={createPrefixFilter((year) => year || 'All Years')}
              value={selectedYear}
              onChange={(event, newValue) => setSelectedYear(newValue || '')}
              renderInput={(params) => <TextField {...params} label="Year" />}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleOpenDialog()}
              // The label is hidden on phones, so the icon-only button needs a
              // name of its own for screen readers.
              aria-label="Add expense"
              sx={{ minWidth: { xs: 44 }, px: { xs: 0.5, sm: 2 }, height: 40 }}
            >
              <AddIcon fontSize="small" sx={{ mr: { xs: 0, sm: 0.75 } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Add Expense
              </Box>
            </Button>
          </Box>
        </Box>

        <Grid container spacing={{ xs: 1, md: 2 }} sx={{ mb: { xs: 1.5, md: 2.5 } }}>
          <Grid item xs={12} md={3} lg={2.5}>
            {/* On a phone this is one ~52px line instead of a 200px card — the
                header was eating over half the viewport before the first row of
                the table appeared. */}
            <SummaryTotalCard label="Total Expenses" value={totalExpenses} formatCurrency={formatCurrency} loading={loading} />
          </Grid>
          <Grid item xs={12} md={9} lg={9.5}>
            <Paper sx={{ ...cardSx, p: { xs: 1.25, md: 2 }, minHeight: summaryPanelMinHeight }}>
              {loading ? (
                <SummaryStripSkeleton />
              ) : sortedSummary.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No category data available{selectedYear ? ` for ${selectedYear}` : ''}.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {/* Category totals — tap one to drill into its expense types */}
                  <Box sx={summaryStripSx}>
                    {sortedSummary.map((item, index) => (
                      <SummaryTile
                        key={`${item.category}-${index}`}
                        label={item.category}
                        amount={item.total}
                        share={maxCategoryTotal ? ((item.total || 0) / maxCategoryTotal) * 100 : 0}
                        selected={selectedCategory === item.category}
                        onClick={() => handleCategoryClick(item.category)}
                        formatCurrency={formatCurrency}
                      />
                    ))}
                  </Box>

                  {/* Breakdown — only rendered when a category is selected. The
                      old code reserved 43px of blank space for it at all times,
                      and showed the tiles with nothing saying what they were. */}
                  {selectedCategory && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: 'text.secondary',
                          }}
                        >
                          {selectedCategory} breakdown
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setSelectedCategory(null)}
                          // Icon-only, so the name has to name the thing it
                          // closes rather than just saying "close".
                          aria-label={`Clear ${selectedCategory} breakdown`}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {getCategoryBreakdown.length > 0 ? (
                        <Box sx={summaryStripSx}>
                          {getCategoryBreakdown.map((item) => (
                            <SummaryTile
                              key={item.expenseTypeId}
                              label={item.expenseTypeName}
                              amount={item.total}
                              share={maxBreakdownTotal
                                ? ((item.total || 0) / maxBreakdownTotal) * 100
                                : 0}
                              formatCurrency={formatCurrency}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No expense types recorded under {selectedCategory}.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {pendingReceipts.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Receipts Awaiting Review ({pendingReceipts.length})
          </Typography>
          <Paper sx={cardSx}>
            {pendingReceipts.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1,
                  px: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                {item.image_filename ? (
                  <ButtonBase
                    onClick={() => setViewingReceiptFilename(item.image_filename)}
                    aria-label="View receipt"
                    sx={{ borderRadius: 1, flexShrink: 0 }}
                  >
                    <Box
                      component="img"
                      src={getMediaUrl(`/api/receipts/${item.image_filename}`)}
                      alt="Receipt thumbnail"
                      sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, display: 'block' }}
                    />
                  </ButtonBase>
                ) : (item.extracted_json?.payee || item.extracted_json?.amount != null) ? (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      flexShrink: 0,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Text
                    </Typography>
                  </Box>
                ) : null}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {item.subject || item.sender || 'Receipt'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.sender}
                    {item.received_at ? ` · ${formatUtcDate(item.received_at)}` : ''}
                  </Typography>
                  {!item.image_filename && (item.extracted_json?.payee || item.extracted_json?.amount != null) && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {[
                        item.extracted_json?.payee,
                        item.extracted_json?.amount != null ? `$${Number(item.extracted_json.amount).toLocaleString()}` : null,
                      ].filter(Boolean).join(' — ')}
                    </Typography>
                  )}
                  {(item.status === 'error' || item.error_message) && (
                    <Typography
                      variant="caption"
                      color={item.status === 'error' ? 'error' : 'warning.main'}
                      display="block"
                    >
                      {item.error_message || 'Could not process this receipt.'}
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={item.status === 'error'}
                  onClick={() => handleReviewPendingReceipt(item)}
                >
                  Review
                </Button>
                <Button size="small" onClick={() => handleDismissPendingReceipt(item.id)}>
                  Dismiss
                </Button>
              </Box>
            ))}
          </Paper>
        </Box>
      )}

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
          {/* The theme's 16px cell padding pushed the Amount column off the
              right edge of a 390px phone — the one number on the row you
              actually came to read. Tighten it on xs only. */}
          <Table
            stickyHeader
            sx={{ '& .MuiTableCell-root': { px: { xs: 1, sm: 2 }, py: { xs: 1, sm: '10px' } } }}
          >
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
                <TableCell sx={{ pl: 1 }}>
                  <TableSortLabel
                    active={sortBy === 'category'}
                    direction={sortBy === 'category' ? sortOrder : 'asc'}
                    onClick={() => handleSort('category')}
                  >
                    Category
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <TableSortLabel
                    active={sortBy === 'expense_type'}
                    direction={sortBy === 'expense_type' ? sortOrder : 'asc'}
                    onClick={() => handleSort('expense_type')}
                  >
                    Expense Type
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <TableSortLabel
                    active={sortBy === 'expense_location'}
                    direction={sortBy === 'expense_location' ? sortOrder : 'asc'}
                    onClick={() => handleSort('expense_location')}
                  >
                    Expense Location
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'expense_date'}
                    direction={sortBy === 'expense_date' ? sortOrder : 'asc'}
                    onClick={() => handleSort('expense_date')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <TableSortLabel
                    active={sortBy === 'expense_time'}
                    direction={sortBy === 'expense_time' ? sortOrder : 'asc'}
                    onClick={() => handleSort('expense_time')}
                  >
                    Time
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'amount'}
                    direction={sortBy === 'amount' ? sortOrder : 'asc'}
                    onClick={() => handleSort('amount')}
                  >
                    Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <TableSortLabel
                    active={sortBy === 'summary'}
                    direction={sortBy === 'summary' ? sortOrder : 'asc'}
                    onClick={() => handleSort('summary')}
                  >
                    Summary
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableSkeletonRows columns={8} />}
              {!loading && sortedExpenses.length === 0 && (
                <TableRow sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      No expenses recorded{selectedYear ? ` for ${selectedYear}` : ''}.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={() => handleOpenDialog()}
                    >
                      Add Expense
                    </Button>
                  </TableCell>
                </TableRow>
              )}
              {sortedExpenses.map((expense) => (
                <TableRow key={expense.id}>
                  <TableCell align="center" sx={actionsKabobCellSx}>
                    <RowActionsMenu
                      actions={[
                        { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(expense) },
                        { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(expense.id), color: 'error' },
                        {
                          key: 'receipt',
                          label: 'View Receipt',
                          icon: <VisibilityIcon fontSize="small" />,
                          onClick: () => setViewingReceiptFilename(expense.receipt_filename),
                          disabled: !expense.has_receipt,
                        },
                      ]}
                    />
                  </TableCell>
                  <TableCell sx={{ pl: 1 }}>{getExpenseCategoryDisplay(expense)}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{getExpenseTypeDisplay(expense)}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{getExpenseLocationDisplay(expense)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', ...tabularNumsSx }}>{formatDate(expense.expense_date) ?? '-'}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, ...tabularNumsSx }}>{formatTime(expense.expense_time)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', ...tabularNumsSx }}>${formatCurrency(expense.amount)}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{expense.summary || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <input
        type="file"
        accept={cameraFirst ? (isIOS ? 'image/*,application/pdf' : 'image/*') : 'image/*,application/pdf'}
        capture={cameraFirst && !isIOS ? 'environment' : undefined}
        ref={receiptInputRef}
        style={{ display: 'none' }}
        onChange={handleScanReceipt}
      />
      <input
        type="file"
        // Mixing in a non-media type (e.g. application/pdf) disqualifies Chrome
        // from using Android's built-in Photo Picker and falls back to the
        // generic app chooser — the same "Select action" dialog the camera
        // input avoids by keeping to a single image type. Kept image-only so
        // this button lands straight in the photo picker with no chooser.
        accept="image/*"
        ref={receiptLibraryInputRef}
        style={{ display: 'none' }}
        onChange={handleScanReceipt}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth={batchSavedExpenses.length > 0 ? 'lg' : 'md'}
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingExpense ? 'Edit Expense' : 'Add New Expense'}
          {cameraFirst ? (
            isIOS ? (
              <IconButton
                onClick={() => receiptInputRef.current?.click()}
                disabled={scanning}
                aria-label="Scan Receipt"
              >
                {scanning ? <CircularProgress size={20} /> : <DocumentScannerIcon />}
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Scan Receipt">
                  <span>
                    <IconButton
                      onClick={() => receiptInputRef.current?.click()}
                      disabled={scanning}
                      aria-label="Scan Receipt"
                    >
                      {scanning ? <CircularProgress size={20} /> : <DocumentScannerIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Load Photo">
                  <span>
                    <IconButton
                      onClick={() => receiptLibraryInputRef.current?.click()}
                      disabled={scanning}
                      aria-label="Load Photo"
                    >
                      <AddPhotoAlternateIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={scanning ? <CircularProgress size={16} /> : <DocumentScannerIcon />}
              onClick={() => receiptInputRef.current?.click()}
              disabled={scanning}
            >
              {scanning ? 'Scanning…' : 'Scan Receipt'}
            </Button>
          )}
        </DialogTitle>
        <DialogContent>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, flex: 1, minWidth: 0 }}>
            {scanError && (
              <Alert severity="error" onClose={() => setScanError('')}>
                {scanError}
              </Alert>
            )}
            {pendingReceiptFile && !scanError && (
              <Alert severity="success">
                Receipt attached: {pendingReceiptFile.name}
              </Alert>
            )}
            {!pendingReceiptFile && activePendingReceiptId && !scanError && (
              <Alert
                severity="success"
                action={activePendingReceipt?.image_filename && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setViewingReceiptFilename(activePendingReceipt.image_filename)}
                  >
                    View
                  </Button>
                )}
              >
                Receipt attached (from email or text)
              </Alert>
            )}
            {potentialDuplicate && (
              <Alert severity="warning" onClose={() => setPotentialDuplicate(null)}>
                Possible duplicate: receipt #{potentialDuplicate.receipt_number || potentialDuplicate.id}
                {potentialDuplicate.expense_date ? ` on ${formatDate(potentialDuplicate.expense_date)}` : ''}
                {potentialDuplicate.amount != null ? ` for $${formatCurrency(potentialDuplicate.amount)}` : ''}
                {potentialDuplicate.payee ? ` at ${potentialDuplicate.payee}` : ''}. Review before saving.
              </Alert>
            )}
            {suggestedTypeName && (
              <Alert
                severity="warning"
                action={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => setOpenExpenseTypeModal(true)}>
                      Create
                    </Button>
                    <Button size="small" onClick={() => setSuggestedTypeName(null)}>
                      Skip
                    </Button>
                  </Box>
                }
              >
                No matching expense type — suggested: "{suggestedTypeName}"
              </Alert>
            )}
            {suggestedLocationName && (
              <Alert
                severity="warning"
                action={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => setOpenExpenseLocationModal(true)}>
                      Create
                    </Button>
                    <Button size="small" onClick={() => setSuggestedLocationName(null)}>
                      Skip
                    </Button>
                  </Box>
                }
              >
                No matching location — suggested: "{suggestedLocationName}"
              </Alert>
            )}
            {scannedItems.length > 0 && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                    Receipt Items
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, width: 220 }}>
                    Item Type
                  </Typography>
                </Box>
                {scannedItems.map((item, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}
                  >
                    <Checkbox
                      size="small"
                      checked={item.included}
                      onChange={() => handleToggleScannedItem(index)}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        minWidth: 120,
                        color: item.included ? 'text.primary' : 'text.disabled',
                        textDecoration: item.included ? 'none' : 'line-through',
                      }}
                    >
                      {item.description}
                      {item.taxable ? ' (taxable)' : ''}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: item.included ? 'text.primary' : 'text.disabled' }}
                    >
                      ${formatCurrency(item.price)}
                    </Typography>
                    <Autocomplete
                      size="small"
                      disableClearable
                      disabled={!item.included}
                      options={expenseTypes}
                      getOptionLabel={(type) => `${type.category}: ${type.name}`}
                      isOptionEqualToValue={(type, value) => type.id === value?.id}
                      filterOptions={createPrefixFilter((type) => `${type.category}: ${type.name}`)}
                      componentsProps={{
                        popper: { disablePortal: true },
                        paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 3 } },
                      }}
                      sx={{
                        width: 220,
                        '& .MuiInputBase-input': { fontSize: '0.875rem' },
                      }}
                      value={expenseTypes.find(
                        (type) => type.id === (item.expense_type_id ?? formData.expense_type_id)
                      ) ?? null}
                      onChange={(e, newValue) => handleSetScannedItemType(index, newValue?.id ?? null)}
                      renderInput={(params) => (
                        <TextField {...params} variant="standard" InputProps={{ ...params.InputProps, disableUnderline: true }} />
                      )}
                    />
                  </Box>
                ))}
                {isSplit && (
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Splits into {groupedItems.length} expenses
                    </Typography>
                    {groupedItems.map((group, i) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" color={group.typeId ? 'text.primary' : 'error'}>
                          {expenseTypeMap.get(group.typeId)
                            ? `${expenseTypeMap.get(group.typeId).category}: ${expenseTypeMap.get(group.typeId).name}`
                            : 'No type assigned'}
                          {' — '}{group.summary}
                        </Typography>
                        <Typography variant="body2" sx={{ flexShrink: 0 }}>
                          ${formatCurrency(group.amount)}
                        </Typography>
                      </Box>
                    ))}
                    {hasUnassignedGroup && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        Assign an expense type to every item before saving.
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>
            )}
            {!isSplit && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  disableClearable
                  options={expenseTypes}
                  getOptionLabel={(type) => `${type.category}: ${type.name}`}
                  isOptionEqualToValue={(type, value) => type.id === value?.id}
                  filterOptions={createPrefixFilter((type) => `${type.category}: ${type.name}`)}
                  componentsProps={{
                    popper: { disablePortal: true },
                    paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 3 } },
                  }}
                  value={expenseTypes.find((type) => type.id === formData.expense_type_id) ?? null}
                  onChange={(e, newValue) =>
                    setFormData((prev) => ({ ...prev, expense_type_id: newValue?.id ?? '' }))
                  }
                  renderInput={(params) => <TextField {...params} label="Expense Type" required />}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setOpenExpenseTypeModal(true)}
                  sx={{ minWidth: 'auto', px: 0.5, height: 40 }}
                >
                  <AddIcon fontSize="small" />
                </Button>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                fullWidth
                size="small"
                disableClearable
                options={expenseLocationOptions}
                getOptionLabel={(loc) => loc.label}
                isOptionEqualToValue={(loc, value) => loc.id === value?.id}
                filterOptions={createPrefixFilter((loc) => loc.label)}
                componentsProps={{
                  popper: { disablePortal: true },
                  paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 3 } },
                }}
                value={expenseLocationOptions.find((loc) => loc.id === formData.expense_location_id) ?? expenseLocationOptions[0]}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, expense_location_id: newValue?.id ?? '' }))
                }
                renderInput={(params) => <TextField {...params} label="Expense Location" />}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setOpenExpenseLocationModal(true)}
                sx={{ minWidth: 'auto', px: 0.5, height: 40 }}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="expense_date"
                  label="Date"
                  type="date"
                  value={formData.expense_date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="expense_time"
                  label="Time"
                  type="time"
                  value={formData.expense_time}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ step: 60 }}
                />
              </Grid>
              {!isSplit && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    name="amount"
                    label="Amount"
                    type="number"
                    value={formData.amount}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    inputProps={{ step: '0.01', min: '0' }}
                  />
                </Grid>
              )}
            </Grid>
            {!isSplit && (
              <TextField
                size="small"
                name="summary"
                label="Summary"
                value={formData.summary}
                onChange={handleInputChange}
                fullWidth
                inputProps={{ maxLength: SUMMARY_MAX_LENGTH }}
              />
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="payee"
                  label="Payee"
                  value={formData.payee}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="receipt_number"
                  label="Receipt #"
                  value={formData.receipt_number}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextField
              size="small"
              name="notes"
              label="Notes"
              value={formData.notes}
              onChange={handleInputChange}
              multiline
              rows={3}
              fullWidth
            />
          </Box>
          {batchSavedExpenses.length > 0 && (
            <Box sx={{ width: 220, flexShrink: 0, borderLeft: 1, borderColor: 'divider', pl: 2, mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Saved this batch ({batchSavedExpenses.length})
              </Typography>
              {/* Row height is pinned to BATCH_ROW_HEIGHT so the list box below can be
                  an exact multiple of it — otherwise a fixed pixel height always ends up
                  slicing whichever row lands on the boundary once enough rows are added. */}
              <Box sx={{ height: BATCH_ROWS_VISIBLE * (BATCH_ROW_HEIGHT + BATCH_ROW_GAP) - BATCH_ROW_GAP, overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${BATCH_ROW_GAP}px` }}>
                  {batchSavedExpenses.map((e) => (
                    <Box
                      key={e.id}
                      sx={{
                        height: BATCH_ROW_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {formatDate(e.expense_date)} - ${formatCurrency(e.amount)}
                      </Typography>
                      <IconButton size="small" onClick={() => handleDeleteClick(e.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          {!editingExpense && (
            <Button
              onClick={handleSubmitAndAddAnother}
              variant="outlined"
              disabled={saving || hasUnassignedGroup}
            >
              Save & Add Another
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || hasUnassignedGroup}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving
              ? (editingExpense ? 'Updating…' : 'Saving…')
              : (editingExpense ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modals for adding reference data */}
      <AddExpenseTypeModal
        open={openExpenseTypeModal}
        onClose={() => { setOpenExpenseTypeModal(false); setSuggestedTypeName(null); setSuggestedTypeCategoryName(null); }}
        apiCall={referenceAPI.createExpenseType}
        initialName={suggestedTypeName || ''}
        initialCategoryName={suggestedTypeCategoryName || ''}
        onSuccess={async (newType) => {
          // The type was created either way; a failed reload must not become an
          // unhandled rejection, and must not stop the new type being selected.
          try {
            const typesRes = await referenceAPI.getExpenseTypes();
            setExpenseTypes(sortExpenseTypes(typesRes?.data));
          } catch (error) {
            console.error('Error reloading expense types:', error);
          }
          // Automatically select the newly created expense type if we have its ID
          if (newType && newType.id) {
            setFormData(prev => ({ ...prev, expense_type_id: newType.id }));
          }
        }}
      />

      <AddExpenseLocationModal
        open={openExpenseLocationModal}
        onClose={() => { setOpenExpenseLocationModal(false); setSuggestedLocationName(null); setSuggestedLocationCityState(null); }}
        apiCall={referenceAPI.createExpenseLocation}
        initialLocationName={suggestedLocationName || ''}
        initialLocation={suggestedLocationCityState || ''}
        onSuccess={async (newLocation) => {
          try {
            const locationsRes = await referenceAPI.getExpenseLocations();
            setExpenseLocations(sortExpenseLocations(locationsRes?.data));
          } catch (error) {
            console.error('Error reloading expense locations:', error);
          }
          // Automatically select the newly created location if we have its ID
          if (newLocation && newLocation.id) {
            setFormData(prev => ({ ...prev, expense_location_id: newLocation.id }));
          }
        }}
      />

      <Dialog open={!!viewingReceiptFilename} onClose={() => setViewingReceiptFilename(null)} maxWidth="md" fullWidth>
        <DialogTitle>Receipt</DialogTitle>
        <DialogContent sx={{ maxHeight: '80vh', overflow: 'auto' }}>
          <Box
            component="img"
            src={getMediaUrl(`/api/receipts/${viewingReceiptFilename}`)}
            alt="Receipt"
            sx={{ width: '100%', borderRadius: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingReceiptFilename(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Expense"
        message="Permanently delete this expense? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default ExpensesPage;
